import React, { useEffect, useRef } from 'react'

/**
 * Custom hook for managing HTML dialog elements with fallback support
 * for browsers that don't fully support the dialog element
 */
export function useDialog(
  isOpen: boolean,
  onClose: () => void
): React.RefObject<HTMLDialogElement> {
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (isOpen) {
      // Check if showModal is supported
      if (typeof dialog.showModal === 'function') {
        try {
          dialog.showModal()
        } catch (error) {
          console.warn('Failed to show modal dialog, using fallback:', error)
          dialog.style.display = 'block'
        }
      } else {
        // Fallback for browsers that don't support dialog
        dialog.style.display = 'block'
        dialog.setAttribute('open', '')
      }
    } else {
      // Check if close is supported
      if (typeof dialog.close === 'function') {
        try {
          dialog.close()
        } catch (error) {
          console.warn('Failed to close modal dialog, using fallback:', error)
          dialog.style.display = 'none'
          dialog.removeAttribute('open')
        }
      } else {
        // Fallback for browsers that don't support dialog
        dialog.style.display = 'none'
        dialog.removeAttribute('open')
      }
    }
  }, [isOpen])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    const handleClose = () => {
      onClose()
    }

    const handleCancel = (e: Event) => {
      e.preventDefault()
      handleClose()
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        handleClose()
      }
    }

    // Add event listeners with fallback support
    try {
      dialog.addEventListener('close', handleClose)
      dialog.addEventListener('cancel', handleCancel)

      // Always add keyboard support
      document.addEventListener('keydown', handleKeyDown)

      return () => {
        dialog.removeEventListener('close', handleClose)
        dialog.removeEventListener('cancel', handleCancel)
        document.removeEventListener('keydown', handleKeyDown)
      }
    } catch (error) {
      console.warn('Failed to add dialog event listeners:', error)

      // Fallback: only keyboard support
      document.addEventListener('keydown', handleKeyDown)

      return () => {
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [onClose])

  return dialogRef
}
